import { useState } from 'react';
import { Chip } from '@mui/material';
import {
  CreateButton,
  Datagrid,
  EditButton,
  FunctionField,
  List,
  ReferenceField,
  TextField,
  TopToolbar,
  useListContext,
  useRecordContext,
  type RaRecord,
} from 'react-admin';
import { useRegion } from '../auth/RegionContext';
import { RequireAccess } from '../auth/RequireAccess';
import { useAccessContext } from '../auth/useAccessContext';
import { hasAccess } from '../auth/permissions';
import type { Placement } from '../dataProvider/types';
import {
  LIFECYCLE_COLOR,
  LIFECYCLE_LABEL,
  SLOT_LABEL,
  formatCompetition,
  formatWindow,
  placementCompetition,
  placementLifecycle,
} from './placementState';

/**
 * Placements list — the last Phase 4 surface and a managed CRUD resource
 * (Charities / promo-codes-shaped). A placement is INTERNAL MERCHANDISING (the
 * platform featuring a brand in a slot for a window); no money is attached, so the
 * whole CRUD sits at editor via placements:write (D-placements).
 *
 * Two things are DERIVED, never stored: the lifecycle chip (scheduled / live /
 * ended, from the window + injected clock) and the overlap / share-of-voice
 * indicator (computed among genuinely competing placements — same slot, same
 * market, overlapping window). There is NO active flag and NO delete: a placement
 * is stopped by ENDING its window from the Edit screen.
 *
 * Region-scoped by the access SCOPE through the one marketsForScope mapping (UK
 * scope shows uk placements, US scope us, GLOBAL both).
 */

/** The derived lifecycle chip for a row, off the injected clock. */
const LifecycleChip = ({ now }: { now: number }) => {
  const record = useRecordContext<Placement>();
  if (!record) return null;
  const state = placementLifecycle(record, now);
  return (
    <Chip
      size="small"
      variant="outlined"
      label={LIFECYCLE_LABEL[state]}
      color={LIFECYCLE_COLOR[state]}
    />
  );
};

/**
 * The derived overlap / share-of-voice cell. Reads the full page of rows from the
 * list context so competition is computed against the OTHER placements in scope,
 * not the row alone.
 */
const CompetitionField = () => {
  const record = useRecordContext<Placement>();
  const { data } = useListContext<Placement>();
  if (!record || !data) return null;
  const competition = placementCompetition(record, data);
  return <span>{formatCompetition(competition, record.slot)}</span>;
};

const PlacementListActions = () => {
  const { role, accessibleRegions } = useAccessContext();
  // Internal merchandising: creating a placement confers no value, so Create gates
  // on the same placements:write an editor holds (not a finer capability).
  const canWrite = hasAccess(
    { role, accessibleRegions },
    { capability: 'placements:write' },
  );
  return <TopToolbar>{canWrite && <CreateButton />}</TopToolbar>;
};

/** The list itself. Exported for testing without the access wrapper; `asOf`
 *  injects the clock the lifecycle chip derives from. */
export const PlacementListView = ({ asOf }: { asOf?: number }) => {
  const { region } = useRegion();
  const { role, accessibleRegions } = useAccessContext();
  // One reference clock for the derived lifecycle chips, captured once.
  const [now] = useState<number>(() => asOf ?? Date.now());
  const canWrite = hasAccess(
    { role, accessibleRegions },
    { capability: 'placements:write' },
  );

  return (
    <List
      resource="placements"
      title="Placements"
      // Scope, not activeRegion: GLOBAL shows both markets (marketsForScope).
      filter={{ region }}
      actions={<PlacementListActions />}
      sort={{ field: 'slot', order: 'ASC' }}
      perPage={50}
      exporter={false}
    >
      {/* No row navigation and no bulk actions: crucially no bulk delete, a
          placement is ended via its window, never destroyed. Edit is a per-row,
          capability-gated link. */}
      <Datagrid rowClick={false} bulkActionButtons={false}>
        <ReferenceField
          label="Merchant"
          source="merchantId"
          reference="merchants"
          link={false}
        >
          <TextField source="name" />
        </ReferenceField>
        <FunctionField
          label="Slot"
          sortBy="slot"
          render={(record: RaRecord) => SLOT_LABEL[(record as Placement).slot]}
        />
        <FunctionField
          label="Market"
          sortBy="market"
          render={(record: RaRecord) => (record.market as string).toUpperCase()}
        />
        <TextField source="weight" label="Weight" />
        <FunctionField
          label="Window"
          render={(record: RaRecord) => formatWindow(record as Placement)}
        />
        <FunctionField
          label="Lifecycle"
          render={() => <LifecycleChip now={now} />}
        />
        <FunctionField
          label="Share of voice"
          render={() => <CompetitionField />}
        />
        {canWrite && <EditButton />}
      </Datagrid>
    </List>
  );
};

/** Gated entry point registered as the `placements` Resource's list. */
export const PlacementList = () => (
  <RequireAccess capability="placements:read">
    <PlacementListView />
  </RequireAccess>
);
